import axiosInstance from '@/app/api/axiosInstance';
import {
  ItemSiteAvailability,
  ItemSiteAvailabilityUpdate,
  ItemSiteAvailabilityBulkUpdateResponse,
  SiteItemsResponse,
} from '@/app/types/itemAvailability';
import { ListQueryParams } from '@/app/types/api';

export const getItemSiteAvailability = async (
  itemId: number
): Promise<ItemSiteAvailability[]> => {
  try {
    const response = await axiosInstance.get<ItemSiteAvailability[]>(
      `/item-availability/item/${itemId}`
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to get site availability for item ${itemId}:`, error);
    throw error;
  }
};

export const updateItemSiteAvailability = async (
  itemId: number,
  data: ItemSiteAvailabilityUpdate
): Promise<ItemSiteAvailabilityBulkUpdateResponse> => {
  try {
    const response = await axiosInstance.post<ItemSiteAvailabilityBulkUpdateResponse>(
      `/item-availability/item/${itemId}/sites`,
      data
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to update site availability for item ${itemId}:`, error);
    throw error;
  }
};

export const getSiteItems = async (
  siteId: number,
  params?: ListQueryParams
): Promise<SiteItemsResponse> => {
  try {
    const response = await axiosInstance.get<SiteItemsResponse>(
      `/item-availability/site/${siteId}`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to get items for site ${siteId}:`, error);
    throw error;
  }
};

// Matrix format endpoints for bulk operations
export interface ItemAvailabilityMatrix {
  items: Array<{
    id: number;
    name: string;
    code: string;
    category_id: number | null;
    category_name: string | null;
  }>;
  sites: Array<{
    id: number;
    name: string;
  }>;
  availability: boolean[][];
  min_stock_levels?: (number | null)[][];
  max_stock_levels?: (number | null)[][];
  reorder_levels?: (number | null)[][];
  pagination?: {
    skip: number;
    limit: number;
    total_items: number;
    total_sites: number;
  };
}

export const getItemAvailabilityMatrix = async (
  params?: { skip?: number; limit?: number; item_ids?: string; site_ids?: string }
): Promise<{ matrix: ItemAvailabilityMatrix }> => {
  try {
    const response = await axiosInstance.get<{ matrix: ItemAvailabilityMatrix }>(
      '/item-availability/matrix',
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get item availability matrix:', error);
    throw error;
  }
};

export interface BulkItemAvailabilityUpdate {
  item_id: number;
  sites: Array<{
    site_id: number;
    is_available: boolean;
    min_stock_level?: number | null;
    max_stock_level?: number | null;
    reorder_level?: number | null;
  }>;
}

export interface BulkItemAvailabilityResponse {
  results: Array<{
    item_id: number;
    created: number;
    updated: number;
    errors: string[];
  }>;
  total_created: number;
  total_updated: number;
  total_errors: number;
}

export const bulkUpdateItemAvailability = async (
  updates: BulkItemAvailabilityUpdate[]
): Promise<BulkItemAvailabilityResponse> => {
  try {
    const response = await axiosInstance.post<BulkItemAvailabilityResponse>(
      '/item-availability/bulk',
      { updates }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to bulk update item availability:', error);
    throw error;
  }
};